import type { FeatureKey } from '@lynx-crypto/contracts/feature-flags';
import { getFeatureDefinition } from '@lynx-crypto/contracts/feature-flags';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

type FeatureGuardProps = {
  feature: FeatureKey;
  children: React.ReactNode;
};

/**
 * Route-level enforcement of an admin feature flag. Layered ON TOP of the
 * feature's existing access guard (e.g. Hunt is wrapped in both ResearchGuard
 * AND FeatureGuard) — this one only adds the admin's on/off + tier override.
 *
 * Uses the SHARED resolver via useFeatureAccess, so the route decision matches
 * the sidebar's show/hide AND the backend's allow/deny exactly.
 *
 * When the feature is unavailable, renders an in-place "unavailable" panel
 * rather than a redirect — the page stays discoverable and the reason is clear.
 * Before flags have loaded we render children (the underlying guard still
 * protects the route); the sidebar already hides the entry, and any backend the
 * page calls is independently enforced, so there is no security gap.
 */
export function FeatureGuard({ feature, children }: FeatureGuardProps) {
  const { canUse, loaded } = useFeatureAccess();

  // Until flags load, defer to the underlying guard + server enforcement.
  if (!loaded) return <>{children}</>;

  if (!canUse(feature)) return <FeatureUnavailable feature={feature} />;

  return <>{children}</>;
}

function FeatureUnavailable({ feature }: { feature: FeatureKey }) {
  const def = getFeatureDefinition(feature);
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-border bg-bg-secondary p-8 text-center">
        <div className="mb-3 text-3xl">🚧</div>
        <h1 className="mb-2 text-lg font-semibold text-text-primary">
          {def.label} is unavailable
        </h1>
        <p className="text-sm text-text-muted">
          This feature has been turned off or restricted by an administrator.
          Check back later or contact an admin if you believe you should have
          access.
        </p>
      </div>
    </div>
  );
}
